/**
 * Vertical cross-section extraction (SPEC §5.6).
 *
 * For lee-side decisions this is more informative than the plan view: it is the
 * only place you can see the separation bubble and the wave structure stacked
 * above each other.
 */

import type { FlowField, TerrainStatic } from './types';

export interface CrossSection {
  /** number of samples along the line */
  ns: number;
  /** number of vertical levels */
  nz: number;
  /** horizontal distance from the start, metres, length ns */
  distance: Float32Array;
  /** terrain elevation at each sample, metres, length ns */
  ground: Float32Array;
  /** height above ground of each level, metres, length nz */
  zAGL: Float32Array;
  /** wind component in the section plane, m/s, [k * ns + s] */
  along: Float32Array;
  /** wind component across the section plane, m/s (positive = to the left) */
  across: Float32Array;
  /** vertical velocity, m/s */
  w: Float32Array;
  /** speed magnitude, m/s */
  speed: Float32Array;
  /** separation strength 0–1, length ns (a surface property) */
  separation: Float32Array;
  /** unit vector of the section direction, for labelling */
  dir: [number, number];
  totalLength: number;
}

/** Bilinear sample of a 2D grid at fractional grid coordinates. */
function sample2d(f: Float32Array, w: number, h: number, x: number, y: number): number {
  const xc = Math.min(w - 1, Math.max(0, x));
  const yc = Math.min(h - 1, Math.max(0, y));
  const x0 = Math.floor(xc);
  const y0 = Math.floor(yc);
  const x1 = Math.min(w - 1, x0 + 1);
  const y1 = Math.min(h - 1, y0 + 1);
  const tx = xc - x0;
  const ty = yc - y0;
  return (
    f[y0 * w + x0] * (1 - tx) * (1 - ty) +
    f[y0 * w + x1] * tx * (1 - ty) +
    f[y1 * w + x0] * (1 - tx) * ty +
    f[y1 * w + x1] * tx * ty
  );
}

/**
 * Extract a section between two points given in fractional grid coordinates.
 * The flow is decomposed into the section plane so the panel can draw true
 * in-plane streamlines rather than a misleading projection.
 */
export function extractCrossSection(
  terrain: TerrainStatic,
  flow: FlowField,
  separation: Float32Array | null,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  ns = 220
): CrossSection {
  const w = terrain.width;
  const h = terrain.height;
  const cs = terrain.cellSize;
  const nz = flow.nz;
  const n2 = w * h;

  const dxCells = x1 - x0;
  const dyCells = y1 - y0;
  const lenCells = Math.hypot(dxCells, dyCells) || 1;
  const ux = dxCells / lenCells;
  const uy = dyCells / lenCells;
  const totalLength = lenCells * cs;

  const distance = new Float32Array(ns);
  const ground = new Float32Array(ns);
  const along = new Float32Array(ns * nz);
  const across = new Float32Array(ns * nz);
  const wOut = new Float32Array(ns * nz);
  const speed = new Float32Array(ns * nz);
  const sep = new Float32Array(ns);

  for (let s = 0; s < ns; s++) {
    const t = ns === 1 ? 0 : s / (ns - 1);
    const gx = x0 + dxCells * t;
    const gy = y0 + dyCells * t;
    distance[s] = t * totalLength;
    // Sea-flattened, so a cross-section over water shows a flat surface
    // rather than the sea floor.
    ground[s] = sample2d(terrain.heights, w, h, gx, gy);
    sep[s] = separation ? sample2d(separation, w, h, gx, gy) : 0;

    for (let k = 0; k < nz; k++) {
      const uu = sample2d(flow.u.subarray(k * n2, (k + 1) * n2), w, h, gx, gy);
      const vv = sample2d(flow.v.subarray(k * n2, (k + 1) * n2), w, h, gx, gy);
      const ww = sample2d(flow.w.subarray(k * n2, (k + 1) * n2), w, h, gx, gy);
      const idx = k * ns + s;
      along[idx] = uu * ux + vv * uy;
      // Left-normal of the section direction.
      across[idx] = -uu * uy + vv * ux;
      wOut[idx] = ww;
      speed[idx] = Math.hypot(uu, vv);
    }
  }

  return {
    ns,
    nz,
    distance,
    ground,
    zAGL: flow.vertical.zc,
    along,
    across,
    w: wOut,
    speed,
    separation: sep,
    dir: [ux, uy],
    totalLength,
  };
}

/**
 * Glide analysis along the section: what glide ratio is needed to get from the
 * start of the line to the end, and whether the terrain in between allows it.
 *
 * Pilots quote glide as `1:R` — R metres forward per metre down. The headline
 * number is the straight start-to-end requirement, but that alone can be
 * dangerously optimistic: a col or a spur partway along has to be cleared too,
 * and the ratio that clears it is the one that actually matters. Both are
 * reported, along with where the profile comes closest to the glide path.
 */
export interface GlideAnalysis {
  startElev: number;
  endElev: number;
  /** metres lost from start to end; negative when the end is higher */
  drop: number;
  distance: number;
  /**
   * Glide ratio needed start-to-end, ignoring what is in between.
   * `Infinity` when the end is at or above the start — no glide reaches it.
   */
  ratio: number;
  /**
   * Glide ratio needed to clear every point of the profile, not just the end.
   * Never smaller than `ratio`. `Infinity` when some terrain stands at or above
   * the start elevation, which no glide angle can clear.
   */
  requiredRatio: number;
  /** true when a straight line at `ratio` stays above the ground throughout */
  clearsTerrain: boolean;
  /** smallest gap between that line and the ground, metres; negative = clipped */
  minClearance: number;
  /** distance along the section at which `minClearance` occurs, metres */
  minClearanceAt: number;
}

export function analyseGlide(cs: CrossSection): GlideAnalysis {
  const startElev = cs.ground[0];
  const endElev = cs.ground[cs.ns - 1];
  const drop = startElev - endElev;
  const distance = cs.totalLength;

  const ratio = drop > 0 ? distance / drop : Infinity;

  // Worst case over the whole profile: at distance d the straight line from the
  // start has fallen d/R, so clearing ground g(d) needs R >= d / (start − g(d)).
  let requiredRatio = ratio;
  for (let s = 1; s < cs.ns; s++) {
    const headroom = startElev - cs.ground[s];
    if (headroom <= 0) {
      // Terrain at or above the launch point: no straight glide clears it.
      requiredRatio = Infinity;
      break;
    }
    const need = cs.distance[s] / headroom;
    if (need > requiredRatio) requiredRatio = need;
  }

  // Clearance measured against the *achievable* start-to-end line.
  let minClearance = Infinity;
  let minClearanceAt = 0;
  if (Number.isFinite(ratio)) {
    for (let s = 0; s < cs.ns; s++) {
      const lineElev = startElev - cs.distance[s] / ratio;
      const gap = lineElev - cs.ground[s];
      if (gap < minClearance) {
        minClearance = gap;
        minClearanceAt = cs.distance[s];
      }
    }
  } else {
    minClearance = NaN;
  }

  return {
    startElev,
    endElev,
    drop,
    distance,
    ratio,
    requiredRatio,
    clearsTerrain: Number.isFinite(minClearance) && minClearance >= 0,
    minClearance,
    minClearanceAt,
  };
}
